
import React from 'react';
import { Investment } from '@/types/investment';
import { formatCurrency } from './utils';

interface AmortizationScheduleProps {
  investment: Investment;
}

const AmortizationSchedule = ({ investment }: AmortizationScheduleProps) => {
  const generateAmortizationSchedule = () => {
    // Use original loan amount if provided, otherwise use current balance
    const startingBalance = investment.originalLoanAmount || investment.currentBalance;
    const balance = startingBalance;
    let currentMonthlyPayment = investment.monthlyPayment;
    const annualRate = investment.interestRate / 100;
    const monthlyRate = annualRate / 12;
    const remainingTerms = investment.remainingTerms;
    const balloonAmount = investment.balloonPaymentAmount || 0;
    const specialPayments = investment.specialPayments || [];
    
    // Calculate payment date if first payment date is provided
    // Parse the date string correctly to avoid timezone issues
    const hasDateInfo = investment.firstPaymentDate;
    let firstPaymentDate = null;
    let startYear = 0;
    let startMonth = 0;
    
    if (hasDateInfo) {
      // Parse date string as YYYY-MM-DD and extract components
      const dateParts = investment.firstPaymentDate.split('-');
      startYear = parseInt(dateParts[0]);
      startMonth = parseInt(dateParts[1]) - 1; // Month is 0-indexed
      firstPaymentDate = new Date(startYear, startMonth, 1);
    }
    
    const schedule = [];
    let currentBalance = balance;
    
    for (let month = 1; month <= remainingTerms; month++) {
      const interestPayment = currentBalance * monthlyRate;
      let principalPayment = currentMonthlyPayment - interestPayment;
      let balloonPayment = 0;
      let extraPrincipal = 0;
      let paymentNote = '';
      
      // Check for special payments at this month
      const specialPayment = specialPayments.find(sp => sp.month === month);
      
      if (specialPayment) {
        if (specialPayment.type === 'balloon') {
          balloonPayment = specialPayment.amount;
          paymentNote = 'Balloon';
        } else if (specialPayment.type === 'principal_paydown') {
          extraPrincipal = specialPayment.amount;
          paymentNote = 'Principal Paydown';
        } else if (specialPayment.type === 'payment_change') {
          paymentNote = `Payment: $${specialPayment.amount.toLocaleString()} → $${specialPayment.newPayment?.toLocaleString()}`;
          currentMonthlyPayment = specialPayment.newPayment || currentMonthlyPayment;
          principalPayment = currentMonthlyPayment - interestPayment;
        }
      }
      
      // If this is the last payment and there's a balloon payment (legacy support)
      if (month === remainingTerms && investment.hasBalloonPayment && balloonAmount > 0 && !specialPayment) {
        balloonPayment = balloonAmount;
        principalPayment = Math.max(0, currentBalance - balloonAmount);
        paymentNote = 'Final Balloon';
      }
      
      const totalPayment = currentMonthlyPayment + balloonPayment + extraPrincipal;
      const newBalance = currentBalance - principalPayment - balloonPayment - extraPrincipal;
      
      // Calculate payment date if we have first payment date
      // Always use the 1st of each month
      let paymentDate = null;
      if (firstPaymentDate) {
        // Add months from the start date
        const monthsToAdd = month - 1;
        const targetYear = startYear + Math.floor((startMonth + monthsToAdd) / 12);
        const targetMonth = (startMonth + monthsToAdd) % 12;
        paymentDate = new Date(targetYear, targetMonth, 1);
      }
      
      schedule.push({
        payment: month,
        paymentDate: paymentDate,
        principalPayment: Math.max(0, principalPayment + extraPrincipal),
        interestPayment: Math.max(0, interestPayment),
        balloonPayment: balloonPayment,
        totalPayment: totalPayment,
        remainingBalance: Math.max(0, newBalance),
        note: paymentNote
      });
      
      currentBalance = newBalance;
      
      // Break if balance reaches zero
      if (currentBalance <= 0.01) break;
    }
    
    return schedule;
  };

  const schedule = generateAmortizationSchedule();
  const propertyAddress = investment.propertyAddress || `${investment.type} - ${investment.id}`;
  const hasDateInfo = !!investment.firstPaymentDate;
  const startingBalance = investment.originalLoanAmount || investment.currentBalance;

  return (
    <div className="p-8 bg-white">
      <div className="text-center mb-6">
        <h1 className="text-2xl font-bold mb-2">Amortization Schedule</h1>
        <h2 className="text-lg text-gray-600 mb-1">{propertyAddress}</h2>
        <div className="text-sm text-gray-500 space-y-1">
          {investment.originalLoanAmount && (
            <p>Original Loan Amount: {formatCurrency(investment.originalLoanAmount)}</p>
          )}
          <p>Current Balance: {formatCurrency(investment.currentBalance)}</p>
          <p>Monthly Payment: {formatCurrency(investment.monthlyPayment)}</p>
          <p>Interest Rate: {investment.interestRate}% Annual</p>
          <p>Remaining Terms: {investment.remainingTerms} months</p>
          {investment.firstPaymentDate && (
            <p>First Payment Date: {new Date(investment.firstPaymentDate).toLocaleDateString()}</p>
          )}
          {investment.hasBalloonPayment && investment.balloonPaymentAmount && (
            <p className="text-yellow-600 font-semibold">Balloon Payment: {formatCurrency(investment.balloonPaymentAmount)}</p>
          )}
        </div>
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full border-collapse border border-gray-300 text-sm table-fixed">
          <thead>
            <tr className="bg-gray-100">
              <th className="border border-gray-300 px-4 py-3 text-center" style={{ width: '80px' }}>Payment #</th>
              {hasDateInfo && <th className="border border-gray-300 px-4 py-3 text-center" style={{ width: '110px' }}>Date</th>}
              <th className="border border-gray-300 px-4 py-3 text-right" style={{ width: '130px' }}>Principal</th>
              <th className="border border-gray-300 px-4 py-3 text-right" style={{ width: '130px' }}>Interest</th>
              {(investment.hasBalloonPayment || schedule.some(s => s.balloonPayment > 0)) && (
                <th className="border border-gray-300 px-4 py-3 text-right bg-yellow-200 text-yellow-800 font-semibold" style={{ width: '130px' }}>Balloon</th>
              )}
              <th className="border border-gray-300 px-4 py-3 text-right" style={{ width: '140px' }}>Total Payment</th>
              <th className="border border-gray-300 px-4 py-3 text-right" style={{ width: '150px' }}>Remaining Balance</th>
              {schedule.some(s => s.note) && (
                <th className="border border-gray-300 px-4 py-3 text-left" style={{ width: '150px' }}>Notes</th>
              )}
            </tr>
          </thead>
          <tbody>
            {schedule.map((payment) => (
              <tr key={payment.payment} className={`hover:bg-gray-50 ${payment.note ? 'bg-blue-50' : ''}`}>
                <td className="border border-gray-300 px-4 py-3 text-center">{payment.payment}</td>
                {hasDateInfo && payment.paymentDate && (
                  <td className="border border-gray-300 px-4 py-3 text-center whitespace-nowrap">
                    {payment.paymentDate.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}
                  </td>
                )}
                <td className="border border-gray-300 px-4 py-3 text-right whitespace-nowrap">
                  {formatCurrency(payment.principalPayment)}
                </td>
                <td className="border border-gray-300 px-4 py-3 text-right whitespace-nowrap">
                  {formatCurrency(payment.interestPayment)}
                </td>
                {(investment.hasBalloonPayment || schedule.some(s => s.balloonPayment > 0)) && (
                  <td className={`border border-gray-300 px-4 py-3 text-right whitespace-nowrap ${
                    payment.balloonPayment > 0 
                      ? 'bg-yellow-100 text-yellow-800 font-bold' 
                      : 'bg-yellow-50'
                  }`}>
                    {payment.balloonPayment > 0 ? formatCurrency(payment.balloonPayment) : '-'}
                  </td>
                )}
                <td className="border border-gray-300 px-4 py-3 text-right font-semibold whitespace-nowrap">
                  {formatCurrency(payment.totalPayment)}
                </td>
                <td className="border border-gray-300 px-4 py-3 text-right whitespace-nowrap">
                  {formatCurrency(payment.remainingBalance)}
                </td>
                {schedule.some(s => s.note) && (
                  <td className="border border-gray-300 px-4 py-3 text-left text-xs text-blue-700 font-semibold">
                    {payment.note || '-'}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      <div className="mt-6 text-center text-xs text-gray-500">
        <p>Generated on {new Date().toLocaleDateString()}</p>
        <p>This schedule is based on current balance and remaining terms</p>
        {investment.hasBalloonPayment && (
          <p className="text-yellow-600 font-semibold">* Balloon payment due with final payment</p>
        )}
      </div>
    </div>
  );
};

export default AmortizationSchedule;
